import React, { useState, useContext } from "react";
import { useHistory } from "react-router-dom";
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  makeStyles,
  Tooltip,
  Box
} from "@material-ui/core";
import {
  DashboardIcon,
  PersonIcon,
  SettingsIcon as AdminIcon,
  HomeIcon as Home,
  PieChartIcon as PieChart,
  BarChartIcon as BarChart,
  TicketIcon as Ticket,
  MessageSquareIcon as MessageSquare,
  UsersIcon as Users,
  TagIcon as Tag,
  WalletIcon as Wallet,
  CalendarIcon as Calendar,
  DashboardIcon as Kanban,
  BotIcon as Bot,
  MegaphoneIcon as Megaphone,
  DollarSignIcon as DollarSign,
  PhoneIcon as Phone,
  ZapIcon as Zap,
  ListIcon,
  WorkflowIcon as Workflow,
  MessageCircleIcon as MessageCircle,
  LandPlotIcon as LandPlot,
  GroupIcon as Group,
  GlobeIcon as Globe,
  LayersIcon as Layers,
  HelpCircleIcon as HelpCircle,
  LinkIcon,
  TrendingUpIcon,
  CatalogIcon
} from "../SvgIcons";
import { AuthContext } from "../../context/Auth/AuthContext";
import { PlanPermissionsContext } from "../../context/PlanPermissions";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  menuButton: {
    color: "rgba(255, 255, 255, 0.9)",
    margin: theme.spacing(0, 0.5),
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
  },
  menuPaper: {
    maxHeight: "70vh",
    maxWidth: "420px",
    minWidth: "360px",
    width: "auto",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
    borderRadius: "12px",
    border: `1px solid ${theme.palette.divider}`,
    overflow: "auto",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: theme.palette.mode === "light" ? "#f1f1f1" : "#2a2a2a",
      borderRadius: "12px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: theme.palette.mode === "light" ? "#888" : "#555",
      borderRadius: "12px",
      "&:hover": {
        background: theme.palette.mode === "light" ? "#555" : "#777",
      },
    },
  },
  categoryHeader: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    position: "sticky",
    top: 0,
    zIndex: 10,
    minHeight: "40px",
    display: "flex",
    alignItems: "center",
    "&:first-child": {
      borderTopLeftRadius: "12px",
      borderTopRightRadius: "12px",
    },
  },
  menuItem: {
    padding: theme.spacing(1, 1.5),
    minHeight: "48px",
    display: "flex",
    alignItems: "center",
    width: "100%",
    boxSizing: "border-box",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      transform: "translateX(4px)",
      transition: "all 0.2s ease",
    },
    transition: "all 0.2s ease",
    "&:last-child": {
      borderBottomLeftRadius: "12px",
      borderBottomRightRadius: "12px",
    },
  },
  menuItemText: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    "& .MuiListItemText-primary": {
      fontSize: "0.875rem",
      fontWeight: 500,
      color: theme.palette.text.primary,
      lineHeight: 1.3,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    "& .MuiListItemText-secondary": {
      fontSize: "0.75rem",
      color: theme.palette.text.secondary,
      lineHeight: 1.2,
      marginTop: "4px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  iconWrapper: {
    minWidth: "36px",
    width: "36px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(1.25),
    flexShrink: 0,
    "& .MuiSvgIcon-root": {
      fontSize: "1.3rem",
    },
  },

  menuContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  menuButtonIcon: {
    fontSize: "1.5rem",
  },
}));

// Hook para usar a lógica de navegação do ActionSearchBar
function useNavigationActions() {
  const { user } = useContext(AuthContext);
  const { permissions } = useContext(PlanPermissionsContext);

  // Organizar ações por categorias usando o contexto de permissões
  const getNavigationCategories = () => {
    if (!user || !permissions) return { dashboard: [], user: [], admin: [] };

    const dashboard = [];
    const userItems = [];
    const admin = [];

    // Dashboard & Gestão
    if (
      (user.profile === "user" && user.showDashboard === "enabled") ||
      user.allowRealTime === "enabled" ||
      user.profile === "admin"
    ) {
      dashboard.push({
        id: "dashboard",
        label: "Dashboard",
        icon: <Home style={{ color: "#2196f3" }} />,
        description: "Painel principal",
        path: "/",
      });
    }

    if ((user.profile === "user" && user.showDashboard === "enabled") || user.profile === "admin") {
      dashboard.push({
        id: "reports",
        label: "Relatórios",
        icon: <PieChart style={{ color: "#009688" }} />,
        description: "Relatórios e análises",
        path: "/reports",
      });
    }

    if ((user.profile === "user" && user.allowRealTime === "enabled") || user.profile === "admin") {
      dashboard.push({
        id: "moments",
        label: "Chats Tempo Real",
        icon: <BarChart style={{ color: "#4caf50" }} />,
        description: "Conversas em tempo real",
        path: "/moments",
      });
    }

    if (user.profile === "admin" && permissions.useWebChat) {
      dashboard.push({
        id: "wallets",
        label: "Carteiras",
        icon: <Wallet style={{ color: "#4caf50" }} />,
        description: "Carteiras digitais",
        path: "/wallets",
      });
    }

    // Itens de Usuário (sempre disponíveis ou com permissão específica)
    userItems.push({
      id: "tickets",
      label: "Tickets",
      icon: <Ticket style={{ color: "#4caf50" }} />,
      description: "Gerenciar tickets",
      path: "/tickets",
    });

    // Módulo Asterisk VoIP
    if (permissions.useAsterisk) {
      userItems.push({
        id: "asterisk-connections",
        label: i18n.t("asterisk.connections.title"),
        icon: <Phone style={{ color: "#4caf50" }} />,
        description: i18n.t("asterisk.settings"),
        path: "/asterisk/connections",
      });

      userItems.push({
        id: "asterisk-calls",
        label: i18n.t("asterisk.callHistory.title"),
        icon: <ListIcon style={{ color: "#2196f3" }} />,
        description: i18n.t("asterisk.callHistory.title"),
        path: "/asterisk/calls",
      });
    }

    userItems.push({
      id: "quick-messages",
      label: "Mensagens Rápidas",
      icon: <MessageSquare style={{ color: "#00bcd4" }} />,
      description: "Respostas rápidas",
      path: "/quick-messages",
    });

    if (permissions.useWhatsappCatalog) {
      console.log("✅ [Catalog Debug] Adicionando item de menu Catálogo WhatsApp");
      userItems.push({
        id: "catalog",
        label: "Catálogo WhatsApp",
        icon: <CatalogIcon style={{ color: "#673ab7" }} />,
        description: "Gerenciar catálogo de produtos",
        path: "/catalog",
      });
    } else {
      console.log("❌ [Catalog Debug] showWhatsappCatalog está FALSE - item NÃO será adicionado ao menu");
    }

    if (permissions.useKanban) {
      userItems.push({
        id: "kanban",
        label: "Kanban",
        icon: <Kanban style={{ color: "#9c27b0" }} />,
        description: "Quadro Kanban",
        path: "/kanban",
      });
    }

    if (user.showContacts === "enabled") {
      userItems.push({
        id: "contacts",
        label: "Contatos",
        icon: <Users style={{ color: "#ff9800" }} />,
        description: "Gerenciar contatos",
        path: "/contacts",
      });
    }

    if (permissions.useSchedules) {
      userItems.push({
        id: "schedules",
        label: "Agendamentos",
        icon: <Calendar style={{ color: "#f44336" }} />,
        description: "Gerenciar agendamentos",
        path: "/schedules",
      });
    }

    userItems.push({
      id: "tags",
      label: "Tags",
      icon: <Tag style={{ color: "#e91e63" }} />,
      description: "Gerenciar tags",
      path: "/tags",
    });

    if (permissions.useInternalChat) {
      userItems.push({
        id: "chats",
        label: "Chats Internos",
        icon: <MessageCircle style={{ color: "#4caf50" }} />,
        description: "Conversas internas",
        path: "/chats",
      });
    }

    // Central de Ajuda - sempre visível
    userItems.push({
      id: "help",
      label: "Central de Ajuda",
      icon: <HelpCircle style={{ color: "#2196f3" }} />,
      description: "Documentação e tutoriais",
      path: "/help",
    });

    // Campanhas e Marketing
    if (user?.showCampaign === "enabled" && permissions.useCampaigns) {
      userItems.push(
        {
          id: "campaigns",
          label: "Campanhas",
          icon: <Megaphone style={{ color: "#ffeb3b" }} />,
          description: "Campanhas de marketing",
          path: "/campaigns",
        },
        {
          id: "contact-lists",
          label: "Listas de Contatos",
          icon: <ListIcon style={{ color: "#ffc107" }} />,
          description: "Listas de contatos",
          path: "/contact-lists",
        }
      );
    }

    // Flowbuilder
    if (user.showFlow === "enabled") {
      userItems.push(
        {
          id: "phrase-lists",
          label: "Fluxo de Campanha",
          icon: <Calendar style={{ color: "#9c27b0" }} />,
          description: "Fluxos de campanha",
          path: "/phrase-lists",
        },
        {
          id: "flowbuilders",
          label: "Fluxo de Conversa",
          icon: <Workflow style={{ color: "#e91e63" }} />,
          description: "Construtor de fluxos",
          path: "/flowbuilders",
        }
      );
    }

    // Landing Pages e Grupos
    if (permissions.useLandingPages) {
      userItems.push({
        id: "landing-pages",
        label: "Landing Pages",
        icon: <LandPlot style={{ color: "#ff5722" }} />,
        description: "Páginas de destino",
        path: "/landing-pages",
      });
    }

    if (permissions.useGroups) {
      userItems.push({
        id: "groups",
        label: "Grupos",
        icon: <Group style={{ color: "#009688" }} />,
        description: "Gerenciar grupos",
        path: "/groups",
      });
    }

    if (permissions.useAssistants) {
      userItems.push({
        id: "assistants",
        label: "Assistentes IA",
        icon: <Bot style={{ color: "#3f51b5" }} />,
        description: "Assistentes virtuais",
        path: "/assistants",
      });
    }

    if (permissions.useIASDR && user.profile === "admin") {
      userItems.push({
        id: "sdr",
        label: "IA SDR",
        icon: <Bot style={{ color: "#00bcd4" }} />,
        description: "Sistema de prospecção automatizada",
        path: "/sdr",
      });
    }

    if ((user.profile === "user" && user.allowConnections === "enabled") || user.profile === "admin") {
      if (user.super) {
        admin.push({
          id: "announcements",
          label: "Anúncios",
          icon: <Megaphone style={{ color: "#f44336" }} />,
          description: "Gerenciar anúncios",
          path: "/announcements",
        });
      }

      if (permissions.useExternalApi) {
        admin.push({
          id: "messages-api",
          label: "API de Mensagens",
          icon: <Zap style={{ color: "#673ab7" }} />,
          description: "API para mensagens",
          path: "/messages-api",
        });
      }

      admin.push({
        id: "users",
        label: "Usuários",
        icon: <Users style={{ color: "#3f51b5" }} />,
        description: "Gerenciar usuários",
        path: "/users",
      });

      admin.push({
        id: "queues",
        label: "Filas",
        icon: <ListIcon style={{ color: "#2196f3" }} />,
        description: "Filas de atendimento",
        path: "/queues",
      });

      if (permissions.useOpenAi) {
        admin.push({
          id: "prompts",
          label: "Prompts IA",
          icon: <Bot style={{ color: "#00bcd4" }} />,
          description: "Prompts de IA",
          path: "/prompts",
        });
      }

      if (permissions.useIntegrations) {
        admin.push({
          id: "queue-integration",
          label: "Integrações",
          icon: <Layers style={{ color: "#9c27b0" }} />,
          description: "Integrações de filas",
          path: "/queue-integration",
        });
      }

      admin.push({
        id: "connections",
        label: "Conexões",
        icon: <Phone style={{ color: "#9c27b0" }} />,
        description: "Conexões WhatsApp",
        path: "/connections",
      });

      if (user.super) {
        admin.push({
          id: "allConnections",
          label: "Todas Conexões",
          icon: <Globe style={{ color: "#2196f3" }} />,
          description: "Todas as conexões",
          path: "/allConnections",
        });
      }

      // Campanhas Config
      if (user?.showCampaign === "enabled" && permissions.useCampaigns) {
        admin.push({
          id: "campaigns-config",
          label: "Config. Campanhas",
          icon: <AdminIcon style={{ color: "#757575" }} />,
          description: "Configurações de campanhas",
          path: "/campaigns-config",
        });
      }

      admin.push({
        id: "utm-generator",
        label: i18n.t("mainDrawer.listItems.utm.generator"),
        icon: <LinkIcon style={{ color: "#00bcd4" }} />,
        description: "Gerador de links com rastreamento UTM",
        path: "/utm/generator",
      });

      admin.push({
        id: "utm-dashboard",
        label: i18n.t("mainDrawer.listItems.utm.dashboard"),
        icon: <TrendingUpIcon style={{ color: "#ff9800" }} />,
        description: "Dashboard de métricas e relatórios UTM",
        path: "/utm/dashboard",
      });

      admin.push({
        id: "financeiro",
        label: "Financeiro",
        icon: <DollarSign style={{ color: "#4caf50" }} />,
        description: "Gestão financeira",
        path: "/financeiro",
      });

      admin.push({
        id: "settings",
        label: "Configurações",
        icon: <AdminIcon style={{ color: "#757575" }} />,
        description: "Configurações do sistema",
        path: "/settings",
      });
    }

    return { dashboard, user: userItems, admin };
  };

  return getNavigationCategories();
}

function AppBarNavigationMenu() {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [dashboardAnchor, setDashboardAnchor] = useState(null);
  const [userAnchor, setUserAnchor] = useState(null);
  const [adminAnchor, setAdminAnchor] = useState(null);

  const { dashboard, user: userItems, admin } = useNavigationActions();

  if (!user) return null;

  const handleMenuClick = (event, setAnchor) => {
    setAnchor(event.currentTarget);
  };

  const handleMenuClose = (setAnchor) => {
    setAnchor(null);
  };

  const handleItemClick = (path, setAnchor) => {
    history.push(path);
    setAnchor(null);
  };

  const renderMenu = (items, anchor, setAnchor, title) => (
    <Menu
      anchorEl={anchor}
      open={Boolean(anchor)}
      onClose={() => handleMenuClose(setAnchor)}
      classes={{ paper: classes.menuPaper }}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      getContentAnchorEl={null}
      MenuListProps={{
        style: {
          padding: 0,
          maxHeight: "65vh",
          overflow: "auto",
        },
      }}
    >
      <Box className={classes.categoryHeader}>
        <Typography variant="subtitle2" fontWeight="bold" noWrap>
          {title}
        </Typography>
      </Box>
      <Divider />
      {items.map((item, index) => (
        <MenuItem
          key={item.id}
          onClick={() => handleItemClick(item.path, setAnchor)}
          className={classes.menuItem}
          divider={index < items.length - 1}
        >
          <ListItemIcon className={classes.iconWrapper}>
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.label}
            secondary={item.description}
            className={classes.menuItemText}
          />
        </MenuItem>
      ))}
    </Menu>
  );

  return (
    <Box className={classes.menuContainer}>
      {/* Dashboard & Gestão */}
      {dashboard.length > 0 && (
        <>
          <Tooltip title="Dashboard & Gestão" placement="bottom">
            <IconButton
              className={classes.menuButton}
              onClick={(e) => handleMenuClick(e, setDashboardAnchor)}
              size="medium"
            >
                <DashboardIcon className={classes.menuButtonIcon} />
            </IconButton>
          </Tooltip>
          {renderMenu(dashboard, dashboardAnchor, setDashboardAnchor, "Dashboard & Gestão")}
        </>
      )}

      {/* Itens de Usuário */}
      {userItems.length > 0 && (
        <>
          <Tooltip title="Ferramentas de Trabalho" placement="bottom">
            <IconButton
              className={classes.menuButton}
              onClick={(e) => handleMenuClick(e, setUserAnchor)}
              size="medium"
            >
                <PersonIcon className={classes.menuButtonIcon} />
            </IconButton>
          </Tooltip>
          {renderMenu(userItems, userAnchor, setUserAnchor, "Ferramentas de Trabalho")}
        </>
      )}

      {/* Administração */}
      {admin.length > 0 && (
        <>
          <Tooltip title="Administração" placement="bottom">
            <IconButton
              className={classes.menuButton}
              onClick={(e) => handleMenuClick(e, setAdminAnchor)}
              size="medium"
            >
                <AdminIcon className={classes.menuButtonIcon} />
            </IconButton>
          </Tooltip>
          {renderMenu(admin, adminAnchor, setAdminAnchor, "Administração")}
        </>
      )}
    </Box>
  );
}

export default AppBarNavigationMenu;